import { Input, Space, Text } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormContext, useWatch } from '../../../../../../../../hooks'
import type { InvoicingSettingsSection } from '../../../../../../enums/invoicingSettingsSection'
import { useTrackInvoicingSettings } from '../../../../../../hooks/useTrackInvoicingSettings'
import type { UploadZervantInvoiceLogoResponse } from '../../../../query-api'
import type { LogoAndBrandingSettingsFormSchema } from '../../../../types/logoAndBrandingSettingsFormSchema'
import { InvoiceLogo } from '../../../InvoiceLogo'

interface DefaultInvoiceLogoProps {
  trackingSection?: InvoicingSettingsSection
}

export const DefaultInvoiceLogo = ({ trackingSection }: DefaultInvoiceLogoProps): ReactElement => {
  const { t } = useTranslation()
  const { trackInvoicingSettings } = useTrackInvoicingSettings({ section: trackingSection })
  const { control, setValue } = useFormContext<LogoAndBrandingSettingsFormSchema>()

  const logoUrl = useWatch({ control, name: 'logoUrl' })

  const handleInvoiceLogoUploadSuccess = useCallback(
    (data: UploadZervantInvoiceLogoResponse) => {
      trackInvoicingSettings('Logo uploaded')
      setValue('logoUrl', data.url, { shouldDirty: true })
      setValue('logoId', String(data.logoId), { shouldDirty: true })
    },
    [setValue, trackInvoicingSettings],
  )

  const handleInvoiceLogoDeleteSuccess = useCallback(() => {
    setValue('logoUrl', null, { shouldDirty: true })
    setValue('logoId', null, { shouldDirty: true })
  }, [setValue])

  return (
    <>
      <Text colorVariant="secondary">{t('external_invoices.appearance_settings.modal.default_logo')}</Text>
      <Space size="s6" />
      <InvoiceLogo
        logoUrl={logoUrl}
        onDeleteSuccess={handleInvoiceLogoDeleteSuccess}
        onUploadSuccess={handleInvoiceLogoUploadSuccess}
      />
      <Input name="logoId" hidden />
      <Input name="logoUrl" hidden />
    </>
  )
}
